import moment from "moment";

/**
 * Compares two dates to determine if they refer to the same date
 * 
 * @param a A date
 * @param b A date
 * 
 * @returns true if dates refer to the same date, otherwise false
 */
export function isSameDate(a: Date, b: Date): boolean {
    return (
        a.getFullYear() === b.getFullYear()
        &&
        a.getMonth() === b.getMonth()
        &&
        a.getDate() === b.getDate()
    );
}

export function isSameDateMoment(a: moment.Moment, b: moment.Moment): boolean {
    return (
        a.year() === b.year()
        &&
        a.month() === b.month()
        &&
        a.date() === b.date()
    );
}

/**
 * Clamps a date so that it is never greater than the current device time.
 *
 * Server timestamps may be slightly ahead of the client clock due to clock
 * skew between devices or between the server and client. Without this clamp,
 * moment's relative-time formatting can render a future-tense string such as
 * "in a few seconds" for posts that were just created.
 *
 * @param date A date or ISO string from the server
 *
 * @returns The original date, or the current device time if the date is in the future
 */
export function clampFutureDate(date: Date | string): Date {
    const parsed = typeof date === "string" ? new Date(date) : date;
    const now = new Date();

    if (parsed.getTime() > now.getTime()) {
        return now;
    }

    return parsed;
}

export function fromNow(date: Date | string, max24h?: boolean, skipClamp?: boolean): string {
    if (!skipClamp) {
        date = clampFutureDate(date);
    }

    const timeLeft = moment(date).fromNow();
    const diffWeeks = Math.abs(moment().diff(date, "weeks"));
    
    if (max24h) {
        const diffHours = Math.abs(moment().diff(date, "hours"));
        if (diffHours > 23) {
            return moment(date).format("LL");
        }
    }

    if (diffWeeks <= 2) {
        return timeLeft;
    } else {
        return moment(date).format("LL");
    }
}